var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { P, PCSS, CustomElement, Watch, Defaults, ViewChild, CustomTypedEvent, CustomEventUtils, PropertyConverters, Animations, Utils, stopPropagationHandler, avoidHandler } from '@pacem/pacem-core';
import { PacemBaseElement } from './types-components';
export const FileUploadEventName = 'fileupload';
export const FileChangeEventName = 'filechange';
class StoragePickEvent extends CustomTypedEvent {
    constructor(file) {
        super('pick', file);
    }
}
export class FileUploadEvent extends CustomTypedEvent {
    constructor(file) {
        super(FileUploadEventName, file);
    }
}
export class FileChangeEvent extends CustomTypedEvent {
    constructor(file) {
        super(FileChangeEventName, file);
    }
}
const ERROR_BINDING = '!$pacem.isNullOrEmpty(:host.value) && :host.invalidFile && $pacem.isNullOrEmpty(:host.url)';
async function computeDirStats(dir, stats = { files: 0, directories: 0, size: 0 }) {
    for await (let [_, entry] of dir.entries()) {
        if (entry.kind === 'file') {
            stats.files++;
            const file = await entry.getFile();
            stats.size += file.size;
        }
        else {
            stats.directories++;
            await computeDirStats(entry, stats);
        }
    }
    return stats;
}
async function computeFileStats(files) {
    const stats = { files: 0, directories: 0, size: 0 };
    for (let f of files) {
        const file = await f.getFile();
        stats.files++;
        stats.size += file.size;
    }
    return stats;
}
/**
 * PacemUploader Component
 */
let PacemUploadElement = class PacemUploadElement extends PacemBaseElement {
    constructor(_tweener = new Animations.TweenService()) {
        super();
        this._tweener = _tweener;
        this.undoCaption = 'undo';
        this.retryCaption = 'retry';
        this.clearCaption = 'clear';
        this.uploading = false;
        this.size = 0;
        this.percentage = .0;
        this.complete = false;
        this.failed = false;
        this.invalidFile = false;
        this._fields = {
            'parallelism': 3,
            'uid': '',
            'ongoing': 0,
            'enqueuer': null,
            'blob': null,
            'retryFrom': 0,
            'chunkSize': 1024 * 128,
            'undone': false
        };
    }
    convertValueAttributeToProperty(attr) {
        return attr; // better assumptions anyone?
    }
    acceptValue(val) {
        this.percentage = 0;
        this.uploading = false;
        this._fileupload.value = '';
    }
    _getMimeIcon(file = this.value, uploading = this.uploading, mode = this.selectMode ?? 'file') {
        if (uploading) {
            return 'sync';
        }
        const hasValue = !Utils.isNullOrEmpty(file);
        switch (mode) {
            case 'directory':
            case 'directory-picker':
                return hasValue ? 'folder' : 'drive_folder_upload';
            case 'multiple':
            case 'multiple-picker':
                return hasValue ? 'files' : 'upload_file';
        }
        if (Utils.isNullOrEmpty(file)) {
            return 'upload_file';
        }
        var filename = file;
        if (typeof file === 'object') {
            filename = file.name;
        }
        const extPattern = /\.([\w]+)(\s|$)/;
        const match = extPattern.exec(filename);
        if (match && match.length) {
            switch (match[1].toLowerCase()) {
                case 'pdf':
                    return 'picture_as_pdf';
                //case 'txt':
                //case 'doc':
                //case 'docx':
                //    return '<i class="pacem-icon">collection_text</i>';
                case 'jpg':
                case 'jpeg':
                case 'gif':
                case 'png':
                    return 'filter';
            }
        }
        return 'filter_none';
    }
    get blob() {
        return this._fields.blob;
    }
    get inputFields() {
        return [this._fileupload];
    }
    getViewValue(val) {
        const value = this._localValue || val;
        if (Utils.isNullOrEmpty(value)) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        let caption = 'name' in value ? value.name : '';
        if (Utils.isArray(value)) {
            caption = value.map(i => i.name).join(', ');
        }
        const stats = this._stats;
        if (Utils.isNullOrEmpty(stats)) {
            return caption;
        }
        const { size, files } = stats;
        return `${caption}\n(${files} file${files === 1 ? '' : 's'}, ${Utils.core.size(size)})`;
    }
    toggleReadonlyView(readonly) {
        this._setupInputElement(readonly);
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._setupInputElement();
        this._fileupload.addEventListener('click', stopPropagationHandler, false);
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first) {
            switch (name) {
                case 'selectMode':
                    this._setupInputElement();
            }
        }
    }
    disconnectedCallback() {
        if (this._fileupload) {
            this._fileupload.removeEventListener('click', stopPropagationHandler, false);
        }
        super.disconnectedCallback();
    }
    _setupInputElement(readonly = this.readonly, mode = this.selectMode ?? 'file') {
        const input = this._fileupload;
        input.removeAttribute('webkitdirectory');
        input.removeAttribute('multiple');
        input.hidden = readonly;
        switch (mode) {
            case 'directory':
                input.setAttribute('webkitdirectory', 'true');
                break;
            case 'multiple':
                input.setAttribute('multiple', 'true');
                break;
            case 'directory-picker':
            case 'file-picker':
            case 'multiple-picker':
                input.hidden = true;
                break;
        }
    }
    /**
     * Triggered from the component's template.
     */
    _dispatchDownload(evt) {
        avoidHandler(evt);
        const value = /* download the file being currently precessed, if available */ this._localValue || this.value, dispatch = (name) => {
            this.dispatchEvent(new CustomEvent('download', { detail: name, bubbles: true, cancelable: false }));
        };
        if (!Utils.isNullOrEmpty(value)) {
            if (typeof value === 'string') {
                // dispatch outside if url is provided
                dispatch(value);
            }
            else {
                if ('content' in value && !Utils.isNullOrEmpty(value.content)) {
                    // download right-away the content when provided
                    Utils.download(Utils.dataURLToBlob('data:application/download;base64,' + value.content), value.name, value.type);
                }
                else if ('name' in value) {
                    if ('kind' in value) {
                        if (value.kind === 'file') {
                            value.getFile().then(file => {
                                Utils.download(file, file.name, file.type);
                            });
                        }
                        else {
                            dispatch(value.name);
                        }
                    }
                    else {
                        // dispatch outside if content is empty
                        dispatch(value.name);
                    }
                }
            }
        }
    }
    reset() {
        super.reset();
        this._localValue = null;
        this.invalidFile = false;
    }
    _validateAll() {
        const stats = this._stats;
        const size = this.maxSize;
        if (size > 0 && size < stats.size) {
            this.invalidFile = true;
            return false;
        }
        // all-righty then
        this.invalidFile = false;
        return true;
    }
    _validateSingleFile(file = this._fileupload.files[0], filename) {
        if (file instanceof File) {
            filename = file.name;
        }
        filename = filename.substring(filename.lastIndexOf('\\') + 1);
        // max size exceeded
        const size = this.maxSize;
        if (size > 0 && size < file.size) {
            this.invalidFile = true;
            return false;
        }
        // pattern unmatched
        const pattern = this.pattern;
        if (pattern && !(new RegExp(pattern, 'i').test(filename))) {
            this.invalidFile = true;
            return false;
        }
        // all-righty then
        this.invalidFile = false;
        return true;
    }
    upload(file, filename, state) {
        if (this._validateSingleFile(file, filename)) {
            return this._upload(file, filename, state);
        }
    }
    async _upload(file, filename, state) {
        var uploader = this, fields = uploader._fields;
        if (!file)
            return;
        if (!filename && file instanceof File) {
            filename = file.name;
        }
        uploader.failed = false;
        fields.undone = false;
        fields.ongoing = 0;
        const blob = file, size = uploader.size = blob.size;
        filename = filename.substring(filename.lastIndexOf('\\') + 1);
        uploader.percentage = .0;
        uploader.complete = false;
        //
        var request = {
            filename, length: size, action: "start", state, type: file.type
        };
        //
        uploader.uploading = true;
        try {
            var r = await uploader._fetch(request);
            if (r.ok) {
                var json = await r.json();
                fields.retryFrom = 0;
                fields.blob = blob;
                fields.uid = json.uid;
                uploader._manage();
            }
            else {
                uploader.uploading = false;
            }
            return r;
        }
        catch (e) {
            uploader.uploading = false;
        }
        finally {
        }
    }
    async _blobToBase64(blob) {
        const result = await Utils.blobToDataURL(blob);
        return result.substring(result.indexOf('base64,') + 7);
    }
    async _doUpload(blob, skip) {
        var uploader = this, fields = uploader._fields;
        fields.ongoing++;
        //
        const chunk = await this._blobToBase64(blob);
        var request = {
            chunk: chunk,
            uid: fields.uid, position: skip,
            action: "do"
        };
        try {
            const r = await uploader._fetch(request);
            if (r.ok) {
                fields.ongoing--;
                if (!!fields.undone)
                    return;
                const json = await r.json();
                const result = json;
                // parallelism > 1 may cause non-ordered responses
                if (result.percentage > this.percentage) {
                    await uploader._tweenPercentage(Math.round(Math.max(1, result.percentage)), 200);
                }
                if (uploader.complete != result.complete) {
                    uploader.complete = result.complete;
                    if (uploader.complete === true) {
                        uploader.uploading = false;
                        uploader.changeHandler(new FileUploadEvent(result));
                    }
                }
            }
            else {
                // error occurred
                fields.retryFrom = skip;
                uploader.failed = true;
                uploader.uploading = false;
            }
        }
        catch (_) {
            // crash
            fields.retryFrom = skip;
            uploader.failed = true;
            uploader.uploading = false;
        }
    }
    _manage() {
        const fields = this._fields, size = this.size, blob = fields.blob, chunkSize = this.chunkSize || fields.chunkSize;
        var start = fields.retryFrom;
        var end = start + chunkSize;
        //
        fields.enqueuer = setInterval(() => {
            if (start < size && !this.failed) {
                const parallelism = this.parallelism || fields.parallelism;
                if (fields.ongoing >= parallelism)
                    return;
                this._doUpload(blob.slice(start, end), start);
                start = end;
                end = start + chunkSize;
            }
            else {
                var input = this._fileupload;
                input.value = '';
                window.clearInterval(fields.enqueuer);
            }
        }, 100);
    }
    async _buildLocalValue(file, filename = file.name, blob = file) {
        return this._localValue = {
            name: filename,
            size: blob.size,
            type: file.type,
            lastModified: Utils.parseDate(file.lastModified).toISOString(),
            content: await this._blobToBase64(blob)
        };
    }
    _canPick(w) {
        return "showDirectoryPicker" in w &&
            (() => {
                try {
                    return w.self === w.top;
                }
                catch {
                    return false;
                }
            })();
    }
    _tryPickDirectory() {
        if (!this._canPick(window)) {
            return;
        }
        window.showDirectoryPicker({ mode: 'read' }).then(directory => {
            this.changeHandler(new StoragePickEvent(directory));
        });
    }
    _tryPickFiles(multiple = this.selectMode === 'multiple-picker', pattern = this.pattern) {
        if (!this._canPick(window)) {
            return;
        }
        window.showOpenFilePicker({ multiple }).then(files => {
            this.changeHandler(new StoragePickEvent(files));
        });
    }
    /**
     * Called from the template.
     * @param evt
     */
    _clickHandler(evt) {
        avoidHandler(evt);
        if (this.readonly) {
            return;
        }
        switch (this.selectMode) {
            case 'directory-picker':
                this._tryPickDirectory();
                break;
            case 'file-picker':
            case 'multiple-picker':
                this._tryPickFiles();
                break;
        }
    }
    async _mimicCompute() {
        // mimic upload
        this.uploading = true; // <- triggers anim
        if (!Utils.isNullOrEmpty(this.value)) {
            this.percentage = 0;
            await Utils.waitForAnimationEnd(this._tuner);
        }
        else {
            await Utils.idle(250);
        }
        await this._tweener.run(0, 100, 500, 0, Animations.Easings.sineInOut, (t, v) => {
            this.percentage = v;
        });
        // reset pct
        this.percentage = .0;
        this.uploading = false;
    }
    onChange(evt) {
        return new Promise(async (resolve, reject) => {
            if (CustomEventUtils.isInstanceOf(evt, FileUploadEvent)) {
                // result of an upload?
                const file = this.value = evt.detail.filename;
                this.dispatchEvent(evt);
                resolve(file);
            }
            else if (CustomEventUtils.isInstanceOf(evt, StoragePickEvent)) {
                // FileSystem API?
                let val = evt.detail;
                let directory, file, files;
                switch (this.selectMode) {
                    case 'directory-picker':
                        directory = val;
                        this._stats = await computeDirStats(directory);
                        break;
                    case 'multiple-picker':
                    case 'file-picker':
                        files = val;
                        if (files.length === 1) {
                            val = files[0];
                        }
                        this._stats = await computeFileStats(files);
                        break;
                    default:
                        // shouldn't be here
                        this._stats = { directories: 0, files: 0, size: 0 };
                        break;
                }
                resolve(this.value = this._localValue = val);
                const valid = this._validateAll();
                this.dispatchEvent(new FileChangeEvent({ directory, file, files, valid }));
            }
            else {
                // HTMLInputElement change event
                let input = this._fileupload;
                if (input.files.length === 0) {
                    // clear
                    this._stats = { directories: 0, files: 0, size: 0 };
                    resolve(this.value = null);
                }
                else {
                    const files = input.files.length;
                    this._stats = { directories: 0, files, size: Array.from(input.files).reduce((acc, file, _) => acc += file.size, 0) };
                    if (files > 1) {
                        // directory or multiple files
                        const selectedFiles = Array.from(input.files);
                        resolve(this.value = this._localValue = selectedFiles);
                        const valid = this._validateAll();
                        this.dispatchEvent(new FileChangeEvent({ files: selectedFiles, valid }));
                    }
                    else {
                        const file = input.files[0], filename = file.name;
                        let blob = file;
                        // validate file
                        if (!this._validateSingleFile(file) && !Utils.isNullOrEmpty(this.url)) {
                            // do not start uploads with an invalid file
                            resolve(this.value = await this._buildLocalValue(file));
                        }
                        else {
                            if (/\.(jpe?g|png)$/i.test(filename) && this.maxImageWidth > 0 && this.maxImageHeight > 0) {
                                blob = await Utils.Images.resize(blob, this.maxImageWidth, this.maxImageHeight, { quality: .8 });
                            }
                            await this._buildLocalValue(file, filename, blob);
                            if (Utils.isNullOrEmpty(this.url)) {
                                // mimic upload
                                await this._mimicCompute();
                                // no direct upload? set the value to the very File
                                resolve(this.value = this._localValue);
                            }
                            else {
                                // upload, then wait for resulting filename (value will be the filename string) 
                                this._upload(blob, input.value);
                                resolve(this.value);
                            }
                        }
                        // dispatch filechange
                        this.dispatchEvent(new FileChangeEvent({ valid: !this.invalidFile, file }));
                    }
                }
            }
        });
    }
    _fetch(request) {
        return fetch(this.url, {
            method: 'POST', credentials: this.fetchCredentials, headers: Utils.extend({ 'Accept': 'application/json', 'Content-Type': 'application/json' }, this.fetchHeaders || {}), body: JSON.stringify(request)
        });
    }
    async _undo(e) {
        e.preventDefault();
        e.stopPropagation();
        var Uploader = this, fields = Uploader._fields, input = Uploader._fileupload;
        clearInterval(fields.enqueuer);
        var request = {
            action: "undo", uid: fields.uid
        };
        try {
            var r = await Uploader._fetch(request);
            if (r.ok) {
                fields.undone = true;
                Uploader.size = 0;
                Uploader._tweenPercentage(.0, 300);
            }
            return r;
        }
        catch (e) {
        }
        finally {
            // This input element accepts a filename, which may only be programmatically set to the empty string(!)
            input.value = '';
            Uploader.uploading = false;
            return r;
        }
    }
    _tweenPercentage(pct, duration = 500) {
        const from = this.percentage;
        return this._tweener.run(from, pct, duration, 0, Animations.Easings.sineInOut, (t, v) => {
            this.percentage = v;
        });
    }
    _retry(e) {
        e.preventDefault();
        e.stopPropagation();
        this.failed = false;
        this._manage();
    }
    _clear(e) {
        e.preventDefault();
        e.stopPropagation();
        this.invalidFile = false;
        const input = this.inputFields[0];
        // https://stackoverflow.com/a/35323290
        input.value = '';
        if (!/safari/i.test(navigator.userAgent)) {
            input.type = '';
            input.type = 'file';
        }
        this.changeHandler(null);
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemUploadElement.prototype, "fetchCredentials", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemUploadElement.prototype, "fetchHeaders", void 0);
__decorate([
    ViewChild('input[type=file]')
], PacemUploadElement.prototype, "_fileupload", void 0);
__decorate([
    ViewChild(`${P}-tuner`)
], PacemUploadElement.prototype, "_tuner", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemUploadElement.prototype, "undoCaption", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemUploadElement.prototype, "retryCaption", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemUploadElement.prototype, "clearCaption", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.String })
], PacemUploadElement.prototype, "pattern", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.String })
], PacemUploadElement.prototype, "url", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "maxImageWidth", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "parallelism", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "chunkSize", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "maxImageHeight", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "maxSize", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.String })
], PacemUploadElement.prototype, "selectMode", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemUploadElement.prototype, "uploading", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "size", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Number })
], PacemUploadElement.prototype, "percentage", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemUploadElement.prototype, "complete", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemUploadElement.prototype, "failed", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemUploadElement.prototype, "invalidFile", void 0);
PacemUploadElement = __decorate([
    CustomElement({
        tagName: P + '-upload', shadow: Defaults.USE_SHADOW_ROOT,
        template: `<${P}-button on-click=":host._dispatchDownload($event)" class="${PCSS}-upload ${PCSS}-viewfinder flat" css-class="{{ {'upload-chosen': !$pacem.isNullOrEmpty(:host.value) && !:host.uploading, 'upload-error': ${ERROR_BINDING}, 'upload-readonly': :host.readonly } }}">

    <${P}-panel class="upload-button" hide="{{ (:host.uploading || :host.failed) && !$pacem.isNullOrEmpty(:host.url) }}">
        <${P}-button tooltip="{{ :host.readonly ? :host.viewValue : 'upload' }}" class="button-circle pos-relative overflow-hidden"
        icon-glyph="{{ :host._getMimeIcon(:host.value, :host.uploading, :host.selectMode) }}"
            on-click=":host._clickHandler($event)"
            on-mouseover="$this.iconGlyph = :host._getMimeIcon(:host.readonly ? :host.value : '', :host.uploading, :host.selectMode)"
            on-mouseout="$this.iconGlyph = :host._getMimeIcon(:host.value, :host.uploading, :host.selectMode)"
            css-class="{{ {'${PCSS}-anim anim-rotate': :host.uploading, 'button-error': :host.invalidFile, 'button-primary': $pacem.isNullOrEmpty(:host.value) && !:host.invalidFile, 'button-success': !Pacem.Utils.isNullOrEmpty(:host.value) && !:host.invalidFile } }}">
            <input type="file" class="${PCSS}-transparent ${PCSS}-clickable pos-absolute absolute-left absolute-right absolute-top absolute-bottom" />
        </${P}-button>
    </${P}-panel>
    <${P}-panel class="upload-button" hide="{{ !:host.failed }}">
        <${P}-button class="button-circle button-flat" icon-glyph="refresh"
        tooltip="{{ :host.retryCaption }}" on-click=":host._retry($event)"><${P}-text text="{{ :host.retryCaption }}"></${P}-text></${P}-button>
    </${P}-panel>
    <${P}-panel class="upload-button" hide="{{ !:host.uploading || $pacem.isNullOrEmpty(:host.url) }}">
        <${P}-button class="button-circle button-flat" icon-glyph="clear" tooltip="{{ :host.undoCaption }}" 
            on-click=":host._undo($event)"><${P}-text text="{{ :host.undoCaption }}"></${P}-text></${P}-button>
    </${P}-panel>

    <${P}-span tooltip="{{ :host.viewValue }}"
                hide="{{ $pacem.isNullOrEmpty(:host.value) || :host.uploading }}" class="upload-data readonly text-reset display-block ${PCSS}-anim text-truncate text-left ${PCSS}-pad pad-right-3" text="{{ :host.viewValue }}"></${P}-span>

    <${P}-panel class="upload-progress hit-none" hide="{{ :host.readonly || (!$pacem.isNullOrEmpty(:host.value) && !:host.uploading) }}">
        <${P}-tuner value="{{ :host.percentage }}" css-class="{{ {'tuner-success': !:host.invalidFile, 'tuner-error': :host.invalidFile} }}" interactive="false"></${P}-tuner>
    </${P}-panel>

    <${P}-button class="button-circle button-flat clear-button" icon-glyph="clear" hide="{{ $pacem.isNullOrEmpty(:host.value) || :host.uploading }}" tooltip="{{ :host.clearCaption }}" on-click=":host._clear($event)"></${P}-button>
        
</${P}-button>`
    })
], PacemUploadElement);
export { PacemUploadElement };
